import { motion } from 'framer-motion';
import React, { useState, useEffect, useRef } from 'react';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaCheck } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import './ProjectDetail.css';
import { getApiUrl } from '../utils/api';

const stringToGradient = (str) => {
  if (!str) return 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h1 = Math.abs(hash % 360);
  const h2 = (h1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 75%, 65%) 0%, hsl(${h2}, 45%, 45%) 100%)`;
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const url = getApiUrl(`/api/projects/${projectId}`);
    fetch(url, {
      headers: {
        'Bypass-Tunnel-Reminder': 'true'
      }
    })
      .then(res => res.json())
      .then(data => {
        console.log("Fetched project detail:", data);
        setProject(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching project detail:", err);
        setLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
      if (window.lenis) {
        window.lenis.scrollTo(0, { immediate: true });
      }
    }
  }, [loading]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  if (loading) {
    return <div className="loading" style={{ padding: '100px', textAlign: 'center', color: '#fff' }}>Loading...</div>;
  }

  if (!project) {
    return (
      <div className="project-not-found">
        <h2>Project Not Found</h2>
        <button onClick={() => navigate('/')}>Go Back Home</button>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  const baseColor = stringToGradient(projectId);

  return (
    <motion.div
      className="project-detail"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Hero Section */}
      <div className="project-hero" style={{ position: 'relative', padding: '100px 20px 60px 20px', textAlign: 'left', background: baseColor }}>
        <motion.button
          className="back-button"
          style={{ position: 'absolute', top: '20px', left: '20px' }}
          onClick={() => {
            navigate('/', { state: { scrollTo: 'projects', activeProjectId: projectId } });
          }}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft /> Back to Projects
        </motion.button>
        <motion.div
          className="project-hero-content"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          style={{ maxWidth: '800px' }}
        >
          <h1 className="project-detail-title" style={{ fontSize: '3.5rem', marginBottom: '20px', color: '#fff', fontWeight: 'bold' }}>{project.title}</h1>
          <p className="project-detail-description" style={{ fontSize: '1.2rem', opacity: 0.9, color: '#ccc', lineHeight: '1.6' }}>{project.description}</p>
        </motion.div>
      </div>

      {/* Content Section */}
      <motion.div
        className="project-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Technologies */}
        <motion.div className="project-section" variants={itemVariants}>
          <h2 className="section-title">Technologies Used</h2>
          <div className="tech-tags">
            {project.technologyUsed?.map((tech, index) => (
              <motion.span
                key={index}
                className="tech-tag-detail"
                whileHover={{ scale: 1.05 }}
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Full Description */}
        <motion.div className="project-section" variants={itemVariants}>
          <h2 className="section-title">About the Project</h2>
          <p className="full-description">
            {project.data?.find(d => d.title === "About the Project")?.description || project.description}
          </p>
        </motion.div>

        {/* Features */}
        {project.data && project.data.filter(feature => feature.title !== "About the Project").length > 0 && (
          <motion.div className="project-section" variants={itemVariants}>
            <h2 className="section-title">Key Features</h2>
            <ul style={{ listStyleType: 'disc', marginLeft: '25px', opacity: 0.9, color: '#ccc' }}>
              {project.data.filter(feature => feature.title !== "About the Project").map((feature, index) => (
                <li key={index} style={{ marginBottom: '10px', fontSize: '1.1rem' }}>
                  <strong>{feature.title}:</strong> {feature.description}
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Project Images */}
        <motion.div className="project-section" variants={itemVariants}>
          <h2 className="section-title">Project Screenshots</h2>
          <div className="images-carousel-wrapper">
            <button className="scroll-button scroll-left" onClick={scrollLeft}>
              <FaChevronLeft />
            </button>
            <div className="images-grid" ref={scrollContainerRef}>
              {project.screenshotUrls?.map((url, index) => (
                <motion.div
                  key={index}
                  className="image-card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -10 }}
                >
                  <div className="image-container">
                    <img 
                      src={url} 
                      alt={project.title}
                      className="project-screenshot"
                      style={{ width: '100%', height: 'auto', borderRadius: '10px' }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
            <button className="scroll-button scroll-right" onClick={scrollRight}>
              <FaChevronRight />
            </button>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div className="project-cta" variants={itemVariants}>
          <h2>Interested in a similar project?</h2>
          <p>Let's discuss how we can help bring your ideas to life</p>
          <motion.button
            className="cta-button-detail"
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get In Touch
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ProjectDetail;

